import React from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';

/**
 * ErrorBoundary - Catches JavaScript errors in child components
 *
 * Usage:
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <YourComponent />
 * </ErrorBoundary>
 *
 * Or with default fallback:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    this.setState({ errorInfo });

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);

    // Generate a simple event ID for reference
    const eventId = `err-${Date.now().toString(36)}`;
    this.setState({ eventId });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    });

    // If onReset prop is provided, call it
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    // Reset error state and navigate to home
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    });

    // Use hash navigation for SPA
    window.location.hash = '';

    // If onNavigateHome prop is provided, call it
    if (this.props.onNavigateHome) {
      this.props.onNavigateHome();
    }
  };

  render() {
    if (this.state.hasError) {
      // If custom fallback is provided, use it
      if (this.props.fallback) {
        return typeof this.props.fallback === 'function'
          ? this.props.fallback({
              error: this.state.error,
              errorInfo: this.state.errorInfo,
              resetError: this.handleReset,
            })
          : this.props.fallback;
      }

      // Default error UI
      return (
        <DefaultErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          eventId={this.state.eventId}
          onReset={this.handleReset}
          onReload={this.handleReload}
          onGoHome={this.handleGoHome}
          showDetails={this.props.showDetails || process.env.NODE_ENV === 'development'}
          level={this.props.level || 'page'}
        />
      );
    }

    return this.props.children;
  }
}

// Default Error Fallback UI Component
const DefaultErrorFallback = ({
  error,
  errorInfo,
  eventId,
  onReset,
  onReload,
  onGoHome,
  showDetails = false,
  level = 'page', // 'page' | 'section' | 'component'
}) => {
  const [showStack, setShowStack] = React.useState(false);

  // Different layouts based on error level
  if (level === 'component') {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
        <div className="flex items-center gap-2 text-red-700">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-medium">Something went wrong</span>
        </div>
        <button
          onClick={onReset}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (level === 'section') {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-2xl">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-red-100 rounded-full">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-red-800">Unable to load this section</h3>
            <p className="text-red-600 text-sm mt-1">
              An error occurred while loading this content.
            </p>
            <div className="flex gap-2 mt-4">
              <button
                onClick={onReset}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Full page error
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-3xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-white text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Bug className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold">Oops! Something went wrong</h1>
          <p className="text-red-100 mt-2">
            We encountered an unexpected error
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 text-center mb-6">
            Don't worry, this has been noted and we're working on it.
            In the meantime, you can try one of the options below.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onReset}
              className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              Try Again
            </button>
            <button
              onClick={onGoHome}
              className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <Home className="w-5 h-5" />
              Go Home
            </button>
          </div>

          <button
            onClick={onReload}
            className="w-full mt-3 px-4 py-2 text-gray-500 text-sm hover:text-gray-700 transition-colors"
          >
            Reload Page
          </button>

          {/* Error Details (Development) */}
          {showDetails && error && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => setShowStack(!showStack)}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
              >
                <AlertTriangle className="w-4 h-4" />
                {showStack ? 'Hide' : 'Show'} Error Details
              </button>

              {showStack && (
                <div className="mt-3 p-4 bg-gray-900 rounded-xl overflow-auto max-h-64">
                  <p className="text-red-400 font-mono text-xs mb-2">
                    {error.toString()}
                  </p>
                  {errorInfo?.componentStack && (
                    <pre className="text-gray-400 font-mono text-xs whitespace-pre-wrap">
                      {errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Event ID */}
          {eventId && (
            <p className="mt-4 text-center text-xs text-gray-400">
              Error ID: {eventId}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Higher-order component for wrapping components with error boundary
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
};

// Section-level error boundary component
export const SectionErrorBoundary = ({ children, onReset }) => (
  <ErrorBoundary level="section" onReset={onReset}>
    {children}
  </ErrorBoundary>
);

// Component-level error boundary component
export const ComponentErrorBoundary = ({ children, onReset }) => (
  <ErrorBoundary level="component" onReset={onReset}>
    {children}
  </ErrorBoundary>
);

export default ErrorBoundary;
